import React, { useEffect, useMemo, useState } from 'react';
import styles from './TaskComponent.module.scss';
import Button from 'components/UI/V3/Button/Button';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { AppointmentOrderItem } from '@spike/order-model';
import { Feeding, FeedingScheduleType, Hydration, Medication, Relief } from 'model/NewMultipleBooking';
import { useApiClientWrapper, useMarketplace, useMasterData } from 'hooks';
import moment, { Moment } from 'moment-timezone';
import { Option } from '@spike/model';
import TypeSelector from './UI/TypeSelector';
import DateSelectorComponent from 'components/CreateBooking/DateSelector/DateSelectorSection/DateSelectorComponent';
import CalendarBooking from 'components/CreateBooking/DateSelector/DateSelectorSection/CalendarBooking';
import FeedingComponent from 'components/CreateBooking/Daycare/FeedingsAndMedications/FeedingComponent';
import MedicationComponent from 'components/CreateBooking/Daycare/FeedingsAndMedications/MedicationComponent';
import ReliefComponent from './Relief/ReliefComponent';
import HydrationComponent from './Hydration/HydrationComponent';
import clsx from 'clsx';
import { Task } from '@spike/task-model';
import type { Hydration as TaskHydration, Relief as TaskRelief } from '@spike/task-model';
import { useDispatch, useSelector } from 'react-redux';
import { NewBookingsStatus, singleRescheduleThunk } from '@spike/new-bookings-action';
import { createTaskDate, createMedicationTaskDate } from 'utils/dateTimeUtils';
import { getFeedingSlotTimes } from 'utils/feedingSlotTimes';
import { getMarketplaceDisabledFeedingMoments } from 'utils/feedingMomentSettings';
import useNonInitialEffect from '@versiondos/hooks';
import { RootState } from 'store';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import { reloadOrderThunk } from '@spike/orders-action';
import Spinner from 'components/UI/V3/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { Title } from 'components/Checkout2/UI/Title';
import { useOrganizationMarketplaces } from 'hooks/useOrganizationMarketplaces';

interface TaskComponentProps {
    task?: Task;
    petId?: number;
    appointments: AppointmentOrderItem[];
    onClose: () => void;
    loading?: boolean;
    defaultTaskType?: 'feeding' | 'medication' | 'relief' | 'hydration';
    onAddTask?: () => void;
}

const getLegacyFeedingTaskType = (feeding?: Task['feeding']): number | undefined =>
    (feeding as { taskType?: number } | undefined)?.taskType;

const getInitialTaskType = (
    task?: Task,
    defaultTaskType?: 'feeding' | 'medication' | 'relief' | 'hydration'
): Option<string> | undefined => {
    if (task?.hydration || getLegacyFeedingTaskType(task?.feeding) === 3) {
        return { id: 'hydration', name: 'hydration' };
    }

    if (task?.relief || getLegacyFeedingTaskType(task?.feeding) === 4) {
        return { id: 'relief', name: 'relief' };
    }

    if (task?.feeding) {
        return { id: 'feeding', name: 'feeding' };
    }

    if (task?.medication) {
        return { id: 'medication', name: 'medication' };
    }

    if (defaultTaskType) {
        return { id: defaultTaskType, name: defaultTaskType };
    }

    return undefined;
};

const isTaskSpecificFeedingTime = (taskFeeding: NonNullable<Task['feeding']>): boolean =>
    (taskFeeding as { specific_time?: boolean; specificTime?: boolean }).specific_time === true ||
    (taskFeeding as { specific_time?: boolean; specificTime?: boolean }).specificTime === true;

const convertTaskFeedingToFeeding = (
    taskFeeding: NonNullable<Task['feeding']>,
    taskDateRaw: Moment | string | undefined,
    timeZone: string,
    presetSlots: { morning: string; afternoon: string; evening: string }
): Feeding => {
    const momentId = taskFeeding.feedingMomentTypeId;
    const taskDate =
        taskDateRaw !== undefined ? (moment.isMoment(taskDateRaw) ? taskDateRaw : moment(taskDateRaw)) : undefined;

    const baseFeeding = (): Feeding => ({
        momentTypes: momentId ? [momentId] : [],
        feedingScheduleType: 'feedingMoment' as FeedingScheduleType,
        foodProvisionType: taskFeeding.foodProvisionerTypeId ?? 1,
        foodType: taskFeeding.foodTypeId,
        quantity: {
            quantity: taskFeeding.foodQuantity ? parseFloat(taskFeeding.foodQuantity) : undefined,
            unit: taskFeeding.foodQuantityUnitId
        },
        brand: taskFeeding.feedingBrand ?? '',
        instructions: taskFeeding.feedingInstructions ?? ''
    });

    if (!momentId || !taskDate?.isValid()) {
        return baseFeeding();
    }

    const dayLocal = taskDate.clone().tz(timeZone).startOf('day');
    const slotStr = momentId === 1 ? presetSlots.morning : momentId === 2 ? presetSlots.afternoon : presetSlots.evening;
    const taskAtTz = taskDate.clone().tz(timeZone);
    const hasPresetSlot = !!String(slotStr ?? '').trim();

    /** Sin specificTime en API (AddPet/AddOrderItem): si la hora ≠ slot del negocio → horario específico */
    if (!hasPresetSlot) {
        if (isTaskSpecificFeedingTime(taskFeeding)) {
            const timeLabel = taskAtTz.format('h:mma').toLowerCase();
            return {
                momentTypes: [momentId],
                feedingScheduleType: 'specificTime',
                specificMomentTimes: { [momentId]: timeLabel },
                foodProvisionType: taskFeeding.foodProvisionerTypeId ?? 1,
                foodType: taskFeeding.foodTypeId,
                quantity: {
                    quantity: taskFeeding.foodQuantity ? parseFloat(taskFeeding.foodQuantity) : undefined,
                    unit: taskFeeding.foodQuantityUnitId
                },
                brand: taskFeeding.feedingBrand ?? '',
                instructions: taskFeeding.feedingInstructions ?? ''
            };
        }
        return baseFeeding();
    }

    const matchesPresetSlot =
        taskAtTz.format('YYYY-MM-DD HH:mm') === createTaskDate(dayLocal, slotStr, timeZone).format('YYYY-MM-DD HH:mm');
    if (matchesPresetSlot) {
        return baseFeeding();
    }

    const timeLabel = taskAtTz.format('h:mma').toLowerCase();
    return {
        momentTypes: [momentId],
        feedingScheduleType: 'specificTime' as FeedingScheduleType,
        specificMomentTimes: { [momentId]: timeLabel },
        foodProvisionType: taskFeeding.foodProvisionerTypeId ?? 1,
        foodType: taskFeeding.foodTypeId,
        quantity: {
            quantity: taskFeeding.foodQuantity ? parseFloat(taskFeeding.foodQuantity) : undefined,
            unit: taskFeeding.foodQuantityUnitId
        },
        brand: taskFeeding.feedingBrand ?? '',
        instructions: taskFeeding.feedingInstructions ?? ''
    };
};

const convertTaskMedicationToMedication = (
    taskMedication: NonNullable<Task['medication']>,
    taskDateRaw: Moment | string | undefined,
    timeZone: string
): Medication => {
    const taskDate =
        taskDateRaw !== undefined ? (moment.isMoment(taskDateRaw) ? taskDateRaw : moment(taskDateRaw)) : undefined;
    const scheduleTime = taskDate?.isValid() ? taskDate.clone().tz(timeZone).format('h:mma').toLowerCase() : '8:00am';

    return {
        name: taskMedication.medicationName ?? '',
        methodType: taskMedication.medicationMethodId,
        dosage: {
            quantity: taskMedication.dosageAmount ? parseFloat(taskMedication.dosageAmount) : undefined,
            unit: taskMedication.dosageUnitId
        },
        instructions: taskMedication.medicationInstructions ?? '',
        schedule: [scheduleTime],
        specificSchedule: []
    };
};

const newMedication = (): Medication => ({
    name: '',
    methodType: undefined,
    dosage: { quantity: undefined, unit: undefined },
    instructions: '',
    schedule: ['8:00am'],
    specificSchedule: []
});

const newFeeding: Feeding = {
    momentTypes: [],
    feedingScheduleType: 'feedingMoment',
    foodProvisionType: 1,
    foodType: undefined,
    quantity: { quantity: undefined, unit: undefined },
    brand: '',
    instructions: ''
};

const newRelief = (): Relief => ({
    momentTypes: [],
    notes: ''
});

const getHydrationTimeOptionsForDate = (
    selectedDate: Moment,
    timeZone: string,
    appointmentFrom?: Moment,
    appointmentTo?: Moment
): string[] => {
    const options: string[] = [];
    const selectedDateInTz = selectedDate.clone().tz(timeZone);
    const selectedDateFormatted = selectedDateInTz.format('YYYY-MM-DD');

    let startHour = 0;
    let startMinute = 0;
    let endHour = 23;
    let endMinute = 59;

    if (appointmentFrom?.isValid() && appointmentTo?.isValid()) {
        const fromTz = appointmentFrom.clone().tz(timeZone);
        const toTz = appointmentTo.clone().tz(timeZone);

        if (selectedDateInTz.isBefore(fromTz, 'day') || selectedDateInTz.isAfter(toTz, 'day')) {
            return options;
        }

        if (selectedDateFormatted === fromTz.format('YYYY-MM-DD')) {
            startHour = fromTz.hour();
            startMinute = fromTz.minute();
        }

        if (selectedDateFormatted === toTz.format('YYYY-MM-DD')) {
            endHour = toTz.hour();
            endMinute = toTz.minute();
        }
    }

    const base = moment.tz(selectedDateFormatted, 'YYYY-MM-DD', timeZone).startOf('day');

    for (let hour = 0; hour <= 23; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            const isWithinStart = hour > startHour || (hour === startHour && minute >= startMinute);
            const isWithinEnd = hour < endHour || (hour === endHour && minute <= endMinute);

            if (isWithinStart && isWithinEnd) {
                options.push(base.clone().hour(hour).minute(minute).format('hh:mm A'));
            }
        }
    }

    return options;
};

const getDefaultHydrationTime = (
    timeZone: string,
    selectedDate: Moment,
    appointmentFrom?: Moment,
    appointmentTo?: Moment
): string => {
    const now = moment().tz(timeZone);
    let baseDate = now.clone();

    if (appointmentFrom?.isValid() && appointmentTo?.isValid()) {
        const fromTz = appointmentFrom.clone().tz(timeZone);
        const toTz = appointmentTo.clone().tz(timeZone);

        if (now.isBefore(fromTz, 'day') || now.isAfter(toTz, 'day')) {
            baseDate = fromTz.clone();
        }
    }

    const options = getHydrationTimeOptionsForDate(selectedDate, timeZone, appointmentFrom, appointmentTo);

    if (options.length === 0) {
        const rounded = baseDate.clone().seconds(0).milliseconds(0);
        const remainder = rounded.minutes() % 15;

        if (remainder !== 0) {
            rounded.add(15 - remainder, 'minutes');
        }

        return rounded.format('h:mma').toLowerCase();
    }

    const currentTime = baseDate.clone().seconds(0).milliseconds(0).format('hh:mm A');
    const exactMatch = options.find(option => option === currentTime);

    if (exactMatch) {
        return moment(exactMatch, 'hh:mm A').format('h:mma').toLowerCase();
    }

    const currentMinutes = baseDate.hour() * 60 + baseDate.minute();
    let closest = options[0];
    let smallestDiff = Infinity;

    for (const option of options) {
        const parsed = moment(option, 'hh:mm A');
        const optionMinutes = parsed.hour() * 60 + parsed.minute();
        const diff = Math.abs(optionMinutes - currentMinutes);

        if (diff < smallestDiff) {
            smallestDiff = diff;
            closest = option;
        }
    }

    return moment(closest, 'hh:mm A').format('h:mma').toLowerCase();
};

const newHydration = (
    timeZone: string,
    selectedDate?: Moment,
    appointmentFrom?: Moment,
    appointmentTo?: Moment
): Hydration => ({
    time: getDefaultHydrationTime(
        timeZone,
        selectedDate ?? moment().tz(timeZone).startOf('day'),
        appointmentFrom,
        appointmentTo
    ),
    notes: ''
});

const createNewHydrationFromAppointment = (
    timeZone: string,
    date: Moment,
    appointmentItem?: AppointmentOrderItem
): Hydration =>
    newHydration(
        timeZone,
        date,
        appointmentItem?.appointment.duration.from?.clone(),
        appointmentItem?.appointment.duration.to?.clone()
    );

const convertTaskToRelief = (task: Task): Relief => {
    if (task.relief) {
        const reliefEntry = task.relief.reliefEntry;
        const momentTypes: number[] = [];

        if (reliefEntry?.morning) {
            momentTypes.push(1);
        } else if (reliefEntry?.afternoon) {
            momentTypes.push(2);
        } else if (reliefEntry?.evening) {
            momentTypes.push(3);
        }

        return {
            momentTypes,
            notes: task.relief.note ?? ''
        };
    }

    if (task.feeding && getLegacyFeedingTaskType(task.feeding) === 4) {
        return {
            momentTypes: task.feeding.feedingMomentTypeId ? [task.feeding.feedingMomentTypeId] : [],
            notes: task.feeding.feedingInstructions ?? ''
        };
    }

    return newRelief();
};

const convertTaskToHydration = (task: Task, timeZone: string): Hydration => {
    if (task.hydration) {
        const taskDate = task.taskDate;
        const parsedDate =
            taskDate !== undefined ? (moment.isMoment(taskDate) ? taskDate : moment(taskDate)) : undefined;

        return {
            time: parsedDate?.isValid()
                ? parsedDate.clone().tz(timeZone).format('h:mma').toLowerCase()
                : getDefaultHydrationTime(
                      timeZone,
                      parsedDate?.isValid()
                          ? parsedDate.clone().tz(timeZone).startOf('day')
                          : moment().tz(timeZone).startOf('day')
                  ),
            notes: task.hydration.note ?? ''
        };
    }

    if (task.feeding && getLegacyFeedingTaskType(task.feeding) === 3) {
        const taskDate = task.taskDate;
        const parsedDate =
            taskDate !== undefined ? (moment.isMoment(taskDate) ? taskDate : moment(taskDate)) : undefined;

        return {
            time: parsedDate?.isValid()
                ? parsedDate.clone().tz(timeZone).format('h:mma').toLowerCase()
                : getDefaultHydrationTime(
                      timeZone,
                      parsedDate?.isValid()
                          ? parsedDate.clone().tz(timeZone).startOf('day')
                          : moment().tz(timeZone).startOf('day')
                  ),
            notes: task.feeding.feedingInstructions ?? ''
        };
    }

    return newHydration(timeZone);
};

const isFeedingTask = (task?: Task): boolean => {
    const legacyTaskType = getLegacyFeedingTaskType(task?.feeding);

    return !!task?.feeding && legacyTaskType !== 3 && legacyTaskType !== 4 && !task.hydration && !task.relief;
};

const getSpecificFeedingMomentIds = (f: Feeding): number[] =>
    f.momentTypes && f.momentTypes.length > 0 ? f.momentTypes : Object.keys(f.specificMomentTimes || {}).map(Number);

const feedingHasSpecificTimesToSave = (f: Feeding): boolean =>
    f.feedingScheduleType === 'specificTime' &&
    getSpecificFeedingMomentIds(f).some(m => !!String(f.specificMomentTimes?.[m] ?? '').trim());

const isDaycareOrBoardingAppointment = (item: AppointmentOrderItem): boolean => {
    const businessAreaId = item.appointment.services[0]?.businessArea?.id;

    return businessAreaId === 'boarding' || businessAreaId === 'daycare';
};

const getDaycareOrBoardingAppointment = (
    appointmentItems: AppointmentOrderItem[],
    options?: { petId?: number; task?: Task }
): AppointmentOrderItem | undefined => {
    const { petId, task } = options ?? {};

    if (task?.appointmentTaskId) {
        return appointmentItems.find(item => item.appointment.id === task.appointmentTaskId);
    }

    const daycareBoardingAppointments = appointmentItems.filter(isDaycareOrBoardingAppointment);

    if (petId !== undefined) {
        const petAppointment = daycareBoardingAppointments.find(item => item.appointment.pet?.id === petId);

        if (petAppointment) {
            return petAppointment;
        }
    }

    return daycareBoardingAppointments[0];
};

const getInitialTaskDate = (
    task: Task | undefined,
    appointments: AppointmentOrderItem[],
    petId: number | undefined,
    timeZone: string
): Moment => {
    if (task?.taskDate) {
        return task.taskDate.clone().tz(timeZone);
    }

    const appointment = getDaycareOrBoardingAppointment(appointments, { petId, task });

    if (appointment?.appointment.duration.from) {
        return appointment.appointment.duration.from.clone().tz(timeZone).startOf('day');
    }

    return moment().tz(timeZone).startOf('day');
};

export const TaskComponent: React.FC<TaskComponentProps> = ({
    loading,
    task,
    petId,
    appointments,
    onClose,
    defaultTaskType,
    onAddTask
}) => {
    const marketplace = useMarketplace();
    const masterData = useMasterData();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { getOrganizationMarketplace, organizationMarketplaces } = useOrganizationMarketplaces();

    const presetSlots = getFeedingSlotTimes(marketplace, masterData, { fallbackToDefaults: true });

    const [type, setType] = useState<Option<string> | undefined>(() => getInitialTaskType(task, defaultTaskType));
    const [appointment, setAppointment] = useState<AppointmentOrderItem | undefined>(() =>
        getDaycareOrBoardingAppointment(appointments, { petId, task })
    );
    const [date, setDate] = useState<Moment>(() =>
        getInitialTaskDate(task, appointments, petId, marketplace.schedule.timeZone)
    );
    const [expanded, setExpanded] = useState(false);
    const [feedingState, setFeedingState] = useState<Feeding | undefined>(() =>
        isFeedingTask(task)
            ? convertTaskFeedingToFeeding(task!.feeding!, task!.taskDate, marketplace.schedule.timeZone, presetSlots)
            : newFeeding
    );
    const [medicationState, setMedicationState] = useState<Medication | undefined>(() =>
        task?.medication
            ? convertTaskMedicationToMedication(task.medication, task.taskDate, marketplace.schedule.timeZone)
            : newMedication()
    );
    const [reliefState, setReliefState] = useState<Relief>(() => {
        if (task && (task.relief || getLegacyFeedingTaskType(task.feeding) === 4)) {
            return convertTaskToRelief(task);
        }

        return newRelief();
    });
    const [hydrationState, setHydrationState] = useState<Hydration>(() => {
        if (task && (task.hydration || getLegacyFeedingTaskType(task.feeding) === 3)) {
            return convertTaskToHydration(task, marketplace.schedule.timeZone);
        }

        const initialAppointment = getDaycareOrBoardingAppointment(appointments, { petId, task });
        const initialDate = getInitialTaskDate(task, appointments, petId, marketplace.schedule.timeZone);

        return createNewHydrationFromAppointment(marketplace.schedule.timeZone, initialDate, initialAppointment);
    });

    const [isLoadingActions, setIsLoadingActions] = useState(false);
    const [success, setSuccess] = useState(false);

    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const selectDateHandler = (selectedDate: Date) => {
        setExpanded(false);
        const taskDate = moment.tz(
            {
                year: selectedDate.getFullYear(),
                month: selectedDate.getMonth(),
                date: selectedDate.getDate(),
                hour: 0,
                minute: 0,
                second: 0
            },
            marketplace.schedule.timeZone
        );
        setDate(taskDate);
    };

    const hydrationDateChangeHandler = (selectedDate: Moment) => {
        setDate(selectedDate.clone().tz(marketplace.schedule.timeZone).startOf('day'));
    };

    const isDisabled = () => {
        if (!type?.name) return true;
        if (!appointment?.appointment?.id) return true;
        if (type?.id === 'feeding') {
            const hasMoments =
                (feedingState?.momentTypes?.length ?? 0) > 0 || feedingHasSpecificTimesToSave(feedingState!);
            if (
                !hasMoments ||
                feedingState?.foodProvisionType === undefined ||
                feedingState?.foodType === undefined ||
                feedingState?.quantity?.quantity === undefined
            ) {
                return true;
            }
        }
        if (
            type?.id === 'medication' &&
            (medicationState?.name === '' ||
                medicationState?.methodType === undefined ||
                medicationState?.dosage?.quantity === undefined)
        )
            return true;
        if (type?.id === 'relief' && (reliefState?.momentTypes?.length ?? 0) === 0) return true;
        if (type?.id === 'hydration' && !hydrationState?.time?.trim()) return true;
        return false;
    };

    const getFeedingsDisabled = (): number[] => {
        const marketplaceDisabled = getMarketplaceDisabledFeedingMoments(marketplace);
        const appointmentFrom = appointment?.appointment.duration.from?.clone().tz(marketplace.schedule.timeZone);
        const appointmentTo = appointment?.appointment.duration.to?.clone().tz(marketplace.schedule.timeZone);

        if (!appointmentFrom?.isValid() || !appointmentTo?.isValid()) {
            return marketplaceDisabled;
        }

        const feedingsDisabled: number[] = [];
        const baseDate = appointmentFrom.clone().startOf('day');

        const morningTime = createTaskDate(baseDate, presetSlots.morning, marketplace.schedule.timeZone);
        const afternoonTime = createTaskDate(baseDate, presetSlots.afternoon, marketplace.schedule.timeZone);
        const eveningTime = createTaskDate(baseDate, presetSlots.evening, marketplace.schedule.timeZone);

        if (!morningTime.isValid() || !afternoonTime.isValid() || !eveningTime.isValid()) {
            return marketplaceDisabled;
        }

        const morningActive = appointmentFrom.isBefore(afternoonTime) && appointmentTo.isSameOrAfter(morningTime);
        const afternoonActive = appointmentFrom.isBefore(eveningTime) && appointmentTo.isSameOrAfter(afternoonTime);
        const eveningActive =
            appointmentFrom.isSameOrAfter(eveningTime) ||
            (appointmentFrom.isBefore(eveningTime) && appointmentTo.isSameOrAfter(eveningTime));

        if (!morningActive) feedingsDisabled.push(1);
        if (!afternoonActive) feedingsDisabled.push(2);
        if (!eveningActive) feedingsDisabled.push(3);

        return Array.from(new Set([...feedingsDisabled, ...marketplaceDisabled]));
    };

    const saveTask = () => {
        const tasksFormatted: Task[] = [...(appointment?.appointment.tasks || [])];

        if (task?.id) {
            const editIndex = tasksFormatted.findIndex(t => t.id === task.id);

            if (editIndex >= 0) {
                tasksFormatted.splice(editIndex, 1);
            }
        }

        if (type?.id === 'feeding' && feedingState) {
            const tz = marketplace.schedule.timeZone;
            const pushFeedingTask = (momentTypeId: number, taskDate: Moment, specificTime: boolean) => {
                tasksFormatted.push({
                    taskDate,
                    id: task?.id,
                    appointmentTaskId: appointment?.appointment.id,
                    feeding: {
                        taskType: 1 as const,
                        feedingMomentTypeId: momentTypeId,
                        foodProvisionerTypeId: feedingState.foodProvisionType,
                        foodTypeId: feedingState.foodType,
                        foodQuantity: feedingState.quantity?.quantity?.toString(),
                        foodQuantityUnitId: feedingState.quantity?.unit,
                        feedingInstructions: feedingState.instructions,
                        feedingBrand: feedingState.brand,
                        roomUnitId: appointment?.appointment.roomUnitId,
                        specificTime: specificTime ? (true as const) : (false as const)
                    }
                });
            };

            if (feedingHasSpecificTimesToSave(feedingState)) {
                getSpecificFeedingMomentIds(feedingState).forEach(momentTypeId => {
                    const timeString = feedingState.specificMomentTimes?.[momentTypeId];
                    if (!timeString?.trim()) return;
                    const taskDate = createMedicationTaskDate(date.clone().tz(tz), String(timeString).trim(), tz);
                    pushFeedingTask(momentTypeId, taskDate, true);
                });
            } else {
                feedingState.momentTypes?.forEach((momentTypeId: number) => {
                    const taskDate = createTaskDate(
                        date,
                        momentTypeId === 1
                            ? presetSlots.morning
                            : momentTypeId === 2
                            ? presetSlots.afternoon
                            : presetSlots.evening,
                        tz
                    );
                    pushFeedingTask(momentTypeId, taskDate, false);
                });
            }
        }

        if (type?.id === 'medication' && medicationState) {
            medicationState.schedule?.forEach((scheduleTime: string) => {
                const taskDate = createMedicationTaskDate(date, scheduleTime, marketplace.schedule.timeZone);

                tasksFormatted.push({
                    taskDate,
                    id: task?.id,
                    appointmentTaskId: appointment?.appointment.id,
                    medication: {
                        taskType: 2 as const,
                        medicationName: medicationState.name,
                        medicationMethodId: medicationState.methodType,
                        dosageAmount: medicationState.dosage?.quantity?.toString(),
                        dosageUnitId: medicationState.dosage?.unit,
                        medicationInstructions: medicationState.instructions,
                        roomUnitId: appointment?.appointment.roomUnitId
                    }
                });
            });
        }

        if (type?.id === 'relief' && reliefState) {
            const tz = marketplace.schedule.timeZone;

            reliefState.momentTypes?.forEach((momentTypeId: number) => {
                const taskDate = createTaskDate(
                    date,
                    momentTypeId === 1
                        ? presetSlots.morning
                        : momentTypeId === 2
                        ? presetSlots.afternoon
                        : presetSlots.evening,
                    tz
                );
                const reliefPayload: TaskRelief = {
                    taskType: 4,
                    reliefEntry: {
                        morning: momentTypeId === 1,
                        afternoon: momentTypeId === 2,
                        evening: momentTypeId === 3
                    },
                    note: reliefState.notes?.trim() ? reliefState.notes : null
                };

                tasksFormatted.push({
                    taskDate,
                    id: task?.id,
                    appointmentTaskId: appointment?.appointment.id,
                    relief: reliefPayload
                });
            });
        }

        if (type?.id === 'hydration' && hydrationState) {
            const tz = marketplace.schedule.timeZone;
            const taskDate = createMedicationTaskDate(date, hydrationState.time, tz);
            const hydrationPayload: TaskHydration = {
                taskType: 3,
                note: hydrationState.notes?.trim() ? hydrationState.notes : null
            };

            tasksFormatted.push({
                taskDate,
                id: task?.id,
                appointmentTaskId: appointment?.appointment.id,
                hydration: hydrationPayload
            });
        }

        dispatch(
            singleRescheduleThunk(
                apiClientWrapper,
                appointment?.appointment.bookingId!,
                appointment?.appointment.id!,
                appointment?.appointment.duration.from.clone()!,
                undefined,
                appointment?.appointment.services[0].id!,
                undefined,
                undefined,
                Number(appointment?.orderItem?.amount),
                appointment?.appointment.duration.to.clone()!,
                undefined,
                false,
                appointment?.appointment.roomUnitId!,
                tasksFormatted,
                undefined
            )
        );
    };

    const cleanTask = () => {
        const resolvedAppointment = getDaycareOrBoardingAppointment(appointments, { petId });
        const taskDate = resolvedAppointment?.appointment.duration.from
            ? resolvedAppointment.appointment.duration.from.clone().tz(marketplace.schedule.timeZone).startOf('day')
            : moment().tz(marketplace.schedule.timeZone).startOf('day');

        setType(getInitialTaskType(undefined, defaultTaskType));
        setAppointment(resolvedAppointment);
        setDate(taskDate);
        setFeedingState(newFeeding);
        setMedicationState(newMedication());
        setReliefState(newRelief());
        setHydrationState(
            createNewHydrationFromAppointment(marketplace.schedule.timeZone, taskDate, resolvedAppointment)
        );
        setExpanded(false);
        setSuccess(false);
    };

    const handleSuccessAddTask = () => {
        if (task && onAddTask) {
            onAddTask();
            return;
        }

        cleanTask();
    };

    const handlerClose = () => {
        cleanTask();
        onClose();
    };

    useEffect(() => {
        if (task) {
            const foundAppointment = getDaycareOrBoardingAppointment(appointments, { task });

            if (foundAppointment) {
                setAppointment(foundAppointment);
            }

            return;
        }

        if (type?.name) {
            setAppointment(getDaycareOrBoardingAppointment(appointments, { petId }));
        }
    }, [task, type?.name, appointments, petId]);

    useNonInitialEffect(() => {
        if (!task) {
            const resolvedAppointment = getDaycareOrBoardingAppointment(appointments, { petId });
            const taskDate = resolvedAppointment?.appointment.duration.from
                ? resolvedAppointment.appointment.duration.from.clone().tz(marketplace.schedule.timeZone).startOf('day')
                : moment().tz(marketplace.schedule.timeZone).startOf('day');

            setDate(taskDate);

            const resetHydrationState = () =>
                setHydrationState(
                    createNewHydrationFromAppointment(marketplace.schedule.timeZone, taskDate, resolvedAppointment)
                );

            if (type?.id === 'feeding') {
                setMedicationState(newMedication);
                setReliefState(newRelief());
                resetHydrationState();
            }

            if (type?.id === 'medication') {
                setFeedingState(newFeeding);
                setReliefState(newRelief());
                resetHydrationState();
            }
            if (type?.id === 'relief') {
                setFeedingState(newFeeding);
                setMedicationState(newMedication());
                resetHydrationState();
            }
            if (type?.id === 'hydration') {
                setFeedingState(newFeeding);
                setMedicationState(newMedication());
                setReliefState(newRelief());
                resetHydrationState();
            }
        }
    }, [type]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SingleRescheduling:
                setIsLoadingActions(true);
                break;
            case NewBookingsStatus.SingleRescheduleSuccess:
                setSuccess(true);
                dispatch(reloadOrderThunk(apiClientWrapper));
                setIsLoadingActions(false);
                break;
            case NewBookingsStatus.Error:
                setIsLoadingActions(false);
        }
    }, [newBookingsStatus]);

    const getMarketplaceName = () => {
        const marketplace = task?.marketplaceId || appointment?.appointment.marketplaceId;
        if (marketplace) {
            const marketplaceData = getOrganizationMarketplace(marketplace);
            return marketplaceData?.name || '';
        }
        return '';
    };

    return (
        <Drawer onClose={handlerClose}>
            <DrawerHeader
                title={
                    success
                        ? ''
                        : task
                        ? 'Edit Task'
                        : type?.id === 'relief'
                        ? 'Add Relief Task'
                        : type?.id === 'hydration'
                        ? 'Add Hydration Task'
                        : 'Add Task'
                }
                onBack={success ? undefined : handlerClose}
            />
            {loading ? (
                <Spinner size={24} />
            ) : (
                <DrawerBody>
                    {success ? (
                        <SuccessScreen
                            title="Good Job"
                            message={task ? 'Your task has been updated.' : 'Your task has been added.'}
                            buttonLabel={task ? 'Add Task' : 'Add Another Task'}
                            buttonVariant="green"
                            onClick={handleSuccessAddTask}
                        />
                    ) : (
                        <>
                            {organizationMarketplaces.length > 1 &&
                                type?.id !== 'relief' &&
                                type?.id !== 'hydration' && (
                                    <div className={clsx(styles.marketplaceContainer, styles.marketplaceContainer)}>
                                        <FontAwesomeIcon
                                            icon={faLocationDot}
                                            className={styles.marketplaceIcon}
                                        />
                                        <Title
                                            title={getMarketplaceName() ? `${getMarketplaceName()}` : ''}
                                            className={styles.marketplaceTitle}
                                        />
                                    </div>
                                )}
                            {!defaultTaskType && type?.id !== 'relief' && type?.id !== 'hydration' && (
                                <div className={styles.section}>
                                    <p className={styles.title}>Type</p>
                                    <TypeSelector
                                        options={[
                                            { id: 'feeding', name: 'Feeding' },
                                            { id: 'medication', name: 'Medication' }
                                        ]}
                                        placeholder="Select Type"
                                        selectedOption={type}
                                        onSelect={setType}
                                    />
                                </div>
                            )}
                            {type?.name && appointment?.appointment?.id && type?.id !== 'hydration' && (
                                <div className={styles.section}>
                                    <p className={styles.title}>Date</p>
                                    <div className={styles.sectionCalendar}>
                                        <span className={styles.text}>{date?.format('dddd, DD MMM YYYY')}</span>
                                        <DateSelectorComponent
                                            id="date"
                                            expanded={expanded}
                                            className={styles.contentDate}
                                            onExpand={() => setExpanded(!expanded)}
                                            onClickAway={() => setExpanded(false)}
                                        >
                                            <CalendarBooking
                                                date={date}
                                                onDaySelect={selectDateHandler}
                                            />
                                        </DateSelectorComponent>
                                    </div>
                                </div>
                            )}
                            {type?.id === 'feeding' && appointment?.appointment?.id && feedingState && (
                                <div className={clsx(styles.section, styles.feedingAndMedicationSection)}>
                                    <FeedingComponent
                                        editing={!!task}
                                        feedingState={feedingState!}
                                        disabled={getFeedingsDisabled()}
                                        onFeedingChange={value => setFeedingState(value as Feeding | undefined)}
                                    />
                                </div>
                            )}
                            {type?.id === 'medication' && appointment?.appointment?.id && medicationState && (
                                <div className={clsx(styles.section, styles.feedingAndMedicationSection)}>
                                    <MedicationComponent
                                        medicationState={medicationState!}
                                        setMedicationState={value =>
                                            setMedicationState(value as Medication | undefined)
                                        }
                                        from={appointment?.appointment.duration.from.clone()}
                                        to={appointment?.appointment.duration.to.clone()}
                                        index={0}
                                        flow="daycare"
                                        medicationType="daily"
                                        onSetMedicationType={() => {}}
                                    />
                                </div>
                            )}
                            {type?.id === 'relief' && appointment?.appointment?.id && (
                                <ReliefComponent
                                    reliefState={reliefState}
                                    disabled={getFeedingsDisabled()}
                                    editing={!!task}
                                    onReliefChange={setReliefState}
                                />
                            )}
                            {type?.id === 'hydration' && appointment?.appointment?.id && (
                                <HydrationComponent
                                    hydrationState={hydrationState}
                                    onHydrationChange={setHydrationState}
                                    date={date}
                                    onDateChange={hydrationDateChangeHandler}
                                />
                            )}
                        </>
                    )}
                </DrawerBody>
            )}
            <DrawerFooter className={styles.footer}>
                <Button
                    label={success ? 'Done' : 'Save'}
                    variant={success ? 'black' : 'green'}
                    size="large"
                    isLoading={isLoadingActions}
                    disabled={success ? false : isDisabled()}
                    onClick={success ? handlerClose : saveTask}
                />
            </DrawerFooter>
        </Drawer>
    );
};

export default TaskComponent;
